import { useCallback } from 'react';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { canPostKitAsset, libraryPostAspect, reelLibraryFile } from '../library/libraryGroups';
import { usePostKitAsset } from '../library/usePostKitAsset';
import { usePostLibraryFile } from '../library/usePostLibraryFile';
import { AssetViewerItem } from './viewerItems';

/**
 * "Post it" of the viewer (design a1): the same rule as the card. A kit asset posts when
 * `canPostKitAsset` is true; a reel posts only where its card has "Post it" (the Library). The
 * viewer closes, then the Composer opens with the file. On Free and the trial a watermarked file
 * opens the choice first (`usePostLibraryFile`).
 */
export function useViewerPost(input: { song: CatalogItem; allowReelPost: boolean; onClose: () => void }): {
  canPost: (item: AssetViewerItem) => boolean;
  post: (item: AssetViewerItem) => void;
} {
  const { song, allowReelPost, onClose } = input;
  const postKitAsset = usePostKitAsset(song);
  const postFile = usePostLibraryFile();

  const canPost = useCallback(
    (item: AssetViewerItem) => {
      if (item.asset !== null) {
        return canPostKitAsset(item.asset);
      }
      const file = item.reel === null ? null : reelLibraryFile(item.reel);
      return allowReelPost && file !== null && libraryPostAspect(file) !== null;
    },
    [allowReelPost]
  );

  const post = useCallback(
    (item: AssetViewerItem) => {
      if (!canPost(item)) {
        return;
      }
      // The viewer closes just before the Composer opens. A watermarked file asks first
      // (design: trial-upsell a2): the viewer stays open under the choice.
      const options = { before: onClose };
      if (item.asset !== null) {
        postKitAsset(item.asset, options);
        return;
      }
      const file = item.reel === null ? null : reelLibraryFile(item.reel);
      if (file !== null) {
        postFile(song, file, options);
      }
    },
    [canPost, onClose, postFile, postKitAsset, song]
  );

  return { canPost, post };
}
